/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ContainerDashboardMatrix,
  ContainerEllipseAccordion,
  ContainerRow,
  ContainerRowUncontrolled,
  FixedContainerHealthProcedure,
  VerticalLine
} from './styles'
import {
  EHealthProceduresTypes,
  IHealthProcedures,
  IHealthProceduresOmics,
  daysPeriodWasSelected,
  monthsPeriodWasSelected,
  yearsPeriodWasSelected,
  createDaysSelectedPeriodWithPrevious,
  createAdmissionEventTagWith,
  IAdmissionRowSlots
} from '~/interfaces'
import { useRadiologyHealthProcedures, useOpenCloseUpdater } from '~/context'
import {
  byTypeEqualToAdmission,
  byTypeExceptAdmission,
  filterHealthProceduresPer,
  hasObjectValue,
  isConduct,
  isOmics,
  timelineFilters
} from '~/utils'
import {
  TimelineOmics,
  TimelineEllipseButton,
  RowContentPart,
  RowInfoPart,
  TimelineYearsAccordion,
  TimelineRectangleButton,
  TimelineConducts
} from '~/components/shared'
import { useDraggableScroll } from '~/hooks/useDraggableScroll'
import {
  mapperTimelineViewByDaysFrom,
  mapperTimelineViewByMonthsFrom,
  mapperTimelineViewByYearsFrom
} from '~/utils/dashboard/timeline/general'
import { isHealthProceduresGroupsEquals, createAdmissionsSlotsFrom } from '~/utils/dashboard/timeline/mappers'

export const TimelineRadiology = (): JSX.Element => {
  const { open: openSidePanel } = useOpenCloseUpdater()
  const {
    displayedHealthProcedures,
    setOpenConductCardList,
    setOpenOmicsCardList,
    typeFiltersActive,
    dateFiltersActive,
    selectedPeriod,
    updateSelectedPeriod,
    selectMedicalEventForDetailing,

    filterTags,
    resetFilterTags,
    setFilterTagToAdmissionEvent,

    sidePanelEvents,
    updateSidePanelEvents,
    resetSidePanelEvents
  } = useRadiologyHealthProcedures()

  const [selectedAdmission, setSelectedAdmission] = useState<IHealthProcedures[]>([])

  const applyTimelineFilters = (healthProcedures: IHealthProcedures[]): IHealthProcedures[] => {
    const searchTerms = filterTags?.filter(tag => tag.type === 'searchTerm').map(tag => tag.text)
    return timelineFilters(healthProcedures)
      .perDate(dateFiltersActive)
      .perType(typeFiltersActive)
      .perMultipleTextTerms(searchTerms).value
  }

  const exitAdmissionView = useCallback(() => {
    updateSidePanelEvents(displayedHealthProcedures)
    setOpenConductCardList(false)
    setOpenOmicsCardList(false)
    setSelectedAdmission([])
    resetFilterTags()
  }, [displayedHealthProcedures, resetFilterTags, setOpenConductCardList, setOpenOmicsCardList, updateSidePanelEvents])

  const enterAdmissionView = useCallback(() => {
    updateSidePanelEvents(selectedAdmission)
    setOpenConductCardList(true)
    setOpenOmicsCardList(true)
  }, [selectedAdmission, setOpenConductCardList, setOpenOmicsCardList, updateSidePanelEvents])

  useEffect(() => {
    if (!selectedAdmission.length) return
    const { period, previousPeriod } = selectedPeriod
    const hasUserManuallyExitAdmissionView = previousPeriod === 'none'
    const hasUserManuallyEnterAdmissionView = period === 'days' && previousPeriod !== 'none'
    if (hasUserManuallyExitAdmissionView) exitAdmissionView()
    if (hasUserManuallyEnterAdmissionView) enterAdmissionView()
  }, [filterTags, enterAdmissionView, exitAdmissionView, selectedAdmission.length, selectedPeriod])

  const timelineRef = useRef(null)
  const { onMouseDown } = useDraggableScroll(timelineRef, { direction: 'vertical' })

  const handleSelectedHealthProcedure = (healthProcedure: IHealthProcedures[]): void => {
    const hasOnlyOneSelectedHealthProcedure = healthProcedure.length === 1
    const isMedicalEventSelectedEqualToTheListedInSidePanel = isHealthProceduresGroupsEquals(
      healthProcedure,
      sidePanelEvents
    )
    const omicsInSelectedHealthProcedure = filterHealthProceduresPer(healthProcedure, isOmics)
    const conductsInSelectedHealthProcedure = filterHealthProceduresPer(healthProcedure, isConduct)

    if (hasOnlyOneSelectedHealthProcedure) {
      selectMedicalEventForDetailing(healthProcedure[0])
      return
    }

    if (isMedicalEventSelectedEqualToTheListedInSidePanel && selectedAdmission.length > 0) {
      updateSidePanelEvents(selectedAdmission)
      return
    }

    if (isMedicalEventSelectedEqualToTheListedInSidePanel && selectedAdmission.length === 0) {
      resetSidePanelEvents()
      return
    }

    if (omicsInSelectedHealthProcedure.length > 0) {
      setOpenOmicsCardList(true)
    }
    if (conductsInSelectedHealthProcedure.length > 0) {
      setOpenConductCardList(true)
    }

    updateSidePanelEvents(healthProcedure)
    openSidePanel()
  }

  const handleSelectedAdmission = (admission: any): void => {
    const { description } = admission[0]
    const healthProceduresWithoutAdmissions = filterHealthProceduresPer(
      displayedHealthProcedures,
      byTypeExceptAdmission
    )
    const admissionSlots = createAdmissionsSlotsFrom([admission[0]], healthProceduresWithoutAdmissions)

    updateSelectedPeriod({ ...createDaysSelectedPeriodWithPrevious(selectedPeriod.period) })
    setFilterTagToAdmissionEvent({ ...createAdmissionEventTagWith(description) })
    setSelectedAdmission(admissionSlots[0].healthProcedures)
  }

  const getTimelineViewByYears = (healthProcedures: IHealthProcedures[]): JSX.Element => {
    const filteredData = applyTimelineFilters(healthProcedures)

    if (!hasObjectValue({ ...filteredData })) return <></>

    const structuredData = mapperTimelineViewByYearsFrom(filteredData)
    return (
      <>
        {structuredData.years.map((year, yearIndex) => {
          return (
            <ContainerEllipseAccordion key={year.id}>
              <TimelineYearsAccordion
                title={`${year.id} (${year.yearRowSlotsTotal})`}
                active={Number(yearIndex) === 0}
                content={
                  <ContainerRowUncontrolled>
                    {year.rowSlots.map(healthProceduresGrouped => {
                      const { healthProcedures } = healthProceduresGrouped

                      if (healthProceduresGrouped.type === EHealthProceduresTypes.ADMISSION) {
                        const admissions = healthProcedures as IAdmissionRowSlots[]
                        return admissions.map(admission => (
                          <FixedContainerHealthProcedure key={admission.id}>
                            <TimelineRectangleButton
                              onClick={handleSelectedAdmission}
                              healthProcedures={[admission]}
                              position={0}
                              rowSize={100}
                              variant={'conduct'}
                            />
                          </FixedContainerHealthProcedure>
                        ))
                      }

                      return (
                        <FixedContainerHealthProcedure key={healthProcedures[0].id}>
                          <TimelineEllipseButton
                            onClick={handleSelectedHealthProcedure}
                            healthProcedures={healthProcedures as IHealthProceduresOmics[]}
                            position={0}
                            quantity={healthProcedures.length === 1 ? 0 : healthProcedures.length}
                          />
                        </FixedContainerHealthProcedure>
                      )
                    })}
                  </ContainerRowUncontrolled>
                }
              />
            </ContainerEllipseAccordion>
          )
        })}
      </>
    )
  }

  const getTimelineViewByMonths = (healthProcedures: IHealthProcedures[]): JSX.Element => {
    const filteredData = applyTimelineFilters(healthProcedures)

    if (!hasObjectValue({ ...filteredData })) return <></>

    const structuredData = mapperTimelineViewByMonthsFrom(filteredData)
    return (
      <>
        {structuredData.years.map((year, yearIndex, yearArray) => {
          return (
            <React.Fragment key={`year-${year.id}`}>
              <ContainerRow>
                <RowInfoPart isFilled={true} period={`${year.id} (${year.yearsRowSlotsTotal})`}>
                  <VerticalLine isFirst={yearIndex === 0} isLast={false} />
                </RowInfoPart>
                <RowContentPart isFull={true} isFilled={false}>
                  {/* HEADER ROW IS EMPTY */}
                </RowContentPart>
              </ContainerRow>

              {year.months.map((month, monthIndex, monthArray) => {
                const healthProceduresWithoutAdmissions = filterHealthProceduresPer(
                  month.rowSlots,
                  byTypeExceptAdmission
                )
                const admissions = filterHealthProceduresPer(month.rowSlots, byTypeEqualToAdmission)

                return (
                  <ContainerRow key={`month-${month.id}`}>
                    <RowInfoPart isFilled={false} period={`${month.id.substring(0, 3)}`}>
                      <VerticalLine
                        isFirst={false}
                        isLast={monthIndex === monthArray.length - 1 && yearIndex === yearArray.length - 1}
                      />
                    </RowInfoPart>
                    <RowContentPart isFull={false} isFilled={false}>
                      {/* CONTENT ROW */}
                      {healthProceduresWithoutAdmissions.length > 0 && (
                        <TimelineOmics
                          period="months"
                          healthProcedures={healthProceduresWithoutAdmissions}
                          handleClick={handleSelectedHealthProcedure}
                        />
                      )}
                      {admissions.length > 0 && (
                        <TimelineConducts
                          healthProcedures={admissions}
                          period="months"
                          handleClick={handleSelectedAdmission}
                        />
                      )}
                    </RowContentPart>
                  </ContainerRow>
                )
              })}
            </React.Fragment>
          )
        })}
      </>
    )
  }

  const getTimelineViewByDays = (healthProcedures: IHealthProcedures[]): JSX.Element => {
    const filteredData = applyTimelineFilters(healthProcedures)

    if (!hasObjectValue({ ...filteredData })) return <></>

    const structuredData = mapperTimelineViewByDaysFrom(filteredData)
    return (
      <>
        {structuredData.months.map((month, monthIndex, monthArray) => {
          return (
            <React.Fragment key={`${month.id}-${new Date(month.date).getFullYear()}`}>
              <ContainerRow>
                <RowInfoPart
                  isFilled={true}
                  period={`${new Date(month.date).getFullYear()}\n${month.id.substring(0, 3)} (${
                    month.monthRowSlotsTotal
                  })`}
                >
                  <VerticalLine isFirst={monthIndex === 0} isLast={false} />
                </RowInfoPart>
                <RowContentPart isFull={true} isFilled={false}>
                  {/* HEADER ROW IS EMPTY */}
                </RowContentPart>
              </ContainerRow>

              {month.days.map((day, dayIndex, dayArray) => {
                return (
                  <ContainerRow key={`${month.id}-${day.id}`}>
                    <RowInfoPart isFilled={false} period={`${day.id}`}>
                      <VerticalLine
                        isFirst={false}
                        isLast={monthIndex === monthArray.length - 1 && dayIndex === dayArray.length - 1}
                      />
                    </RowInfoPart>

                    <RowContentPart isFull={false} isFilled={false}>
                      {/* CONTENT ROW */}
                      <TimelineOmics
                        period="days"
                        healthProcedures={day.rowSlots}
                        handleClick={handleSelectedHealthProcedure}
                      />
                    </RowContentPart>
                  </ContainerRow>
                )
              })}
            </React.Fragment>
          )
        })}
      </>
    )
  }

  return (
    <ContainerDashboardMatrix ref={timelineRef} onMouseDown={onMouseDown}>
      {daysPeriodWasSelected(selectedPeriod)
        ? getTimelineViewByDays(selectedAdmission.length ? selectedAdmission : displayedHealthProcedures)
        : null}
      {monthsPeriodWasSelected(selectedPeriod)
        ? getTimelineViewByMonths(selectedAdmission.length ? selectedAdmission : displayedHealthProcedures)
        : null}
      {yearsPeriodWasSelected(selectedPeriod)
        ? getTimelineViewByYears(selectedAdmission.length ? selectedAdmission : displayedHealthProcedures)
        : null}
    </ContainerDashboardMatrix>
  )
}
